// A feed: closing soon, new, best fit, upcoming (forecasts, briefings, news), endings (contracts ending soon) and
// events (proposers days, industry days and the like).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { api, qs } from "../api/client";
import type { Feed, FeedItems, NoticeItem } from "../api/types";
import { useUI } from "../app/context";
import { keys, useFeeds, useSession } from "../app/queries";
import { Face } from "../components/Badge";
import { GlobalSearch } from "../components/GlobalSearch";
import { EndingRow, EventRow, NoticeRow, UpcomingRow } from "../components/Rows";
import { days, fmt, initials, PATH_NAMES, when, WORK_NAMES } from "../lib/format";
import { FeedEditor } from "./FeedEditor";

type View = "soon" | "new" | "all" | "upcoming" | "endings" | "events";
const VIEWS: [View, string][] = [
  ["soon", "Closing soon"],
  ["new", "New"],
  ["all", "Best fit"],
  ["upcoming", "Upcoming"],
  ["endings", "Endings"],
  ["events", "Events"],
];

export function FeedPage() {
  const { id } = useParams();
  const feeds = useFeeds();
  if (feeds.isPending) return <p className="empty">Loading…</p>;
  const feed = feeds.data?.feeds.find((f) => f.id === Number(id));
  if (!feed) return <Navigate to="/brief" replace />;
  return <FeedView key={feed.id} feed={feed} since={feeds.data!.since} />;
}

function FeedView({ feed: f, since }: { feed: Feed; since: string }) {
  const me = useSession();
  const [params, setParams] = useSearchParams();
  const view = (params.get("view") as View) || "soon";
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<false | "all" | "shape">(false);
  const client = useQueryClient();
  const { showToast } = useUI();
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(q.trim()), 150);
    return () => window.clearTimeout(t);
  }, [q]);
  const items = useQuery({
    queryKey: keys.feedItems(f.id, view, query),
    queryFn: () => api<FeedItems>(`/api/feeds/${f.id}/items${qs({ view, q: query })}`),
  });
  const alerts = useQuery({
    queryKey: ["alerts", "settings"],
    queryFn: () => api<{ feeds: { id: number; frequency: string }[] }>("/api/alerts/settings"),
  });
  const learning = useQuery({
    queryKey: ["feed", f.id, "learning"],
    queryFn: () => api<{ marks: number; exclusions: number; rules: { id: number }[] }>(`/api/feeds/${f.id}/learning`),
    enabled: f.editable,
  });
  const frequency = alerts.data?.feeds.find((x) => x.id === f.id)?.frequency ?? "off";
  const shareText =
    f.visibility === "tenant"
      ? `Shared with everyone at ${me.tenant?.name}`
      : f.visibility === "people"
        ? `Shared with ${f.shared_with.length} ${f.shared_with.length === 1 ? "person" : "people"}`
        : "Only you";
  const follow = async () => {
    await api(`/api/feeds/${f.id}/follow`, { method: "POST", body: { on: !f.following } });
    await client.invalidateQueries({ queryKey: keys.feeds });
    showToast(f.following ? `${f.name} is no longer in your brief.` : `${f.name} is now in your daily brief.`);
  };
  const setAlerts = async (value: string) => {
    await api(`/api/feeds/${f.id}/alerts`, { method: "PUT", body: { frequency: value } });
    await client.invalidateQueries({ queryKey: ["alerts", "settings"] });
    showToast(
      value === "off" ? `Alerts off for ${f.name}.` : `You'll get alerts ${value === "daily" ? "in a daily digest" : "as new records arrive"} for ${f.name}.`,
    );
  };
  const l = learning.data;
  return (
    <>
      <div className="top">
        <div className="date">{f.mine ? "Your feed" : "Shared by " + f.owner_name}</div>
        <GlobalSearch />
      </div>
      <h1>{f.name}</h1>
      <p className="lede">{(f.description || "").replace(/\s*Keywords:.*$/s, "") || "No description yet. Describe what belongs here so GovChew can find it."}</p>
      <div className="feed-meta">
        <Face id={f.owner_id} text={initials(f.owner_name)} />
        <span>{f.owner_name}</span>
        <span className="pill">{shareText}</span>
        {f.show_sole_source ? <span className="pill">Includes sole source</span> : null}
        <span id="learned-note">
          {l && (l.exclusions || l.rules.length) ? `${l.exclusions} excluded${l.rules.length ? `, ${l.rules.length} rule${l.rules.length > 1 ? "s" : ""}` : ""}` : ""}
        </span>
      </div>
      <div className="tools">
        <div className="seg" role="group" aria-label="Show">
          {VIEWS.map(([v, label]) => (
            <button key={v} data-view={v} aria-pressed={view === v} onClick={() => setParams(v === "soon" ? {} : { view: v }, { replace: true })}>
              {label}
              {v === "upcoming" && f.upcoming ? <span className="seg-n"> {f.upcoming}</span> : null}
              {v === "endings" && f.endings ? <span className="seg-n"> {f.endings}</span> : null}
              {v === "events" && f.events ? <span className="seg-n"> {f.events}</span> : null}
            </button>
          ))}
        </div>
        <input className="filter" id="feed-q" type="search" placeholder="Filter this feed" aria-label="Filter this feed" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="tool-btns">
          {f.mine ? null : (
            <button className="btn" id="follow" aria-pressed={f.following} onClick={follow}>
              {f.following ? "Following" : "Follow"}
            </button>
          )}
          <label className="alert-pick">
            🔔{" "}
            <select id="feed-alerts" aria-label="Alerts for this feed" value={frequency} onChange={(e) => setAlerts(e.target.value)}>
              <option value="off">Alerts off</option>
              <option value="daily">Daily digest</option>
              <option value="instant">As they arrive</option>
            </select>
          </label>
          {f.editable ? (
            <button className="btn" id="edit" onClick={() => setEditing("all")}>
              Edit feed
            </button>
          ) : null}
        </div>
      </div>
      <Hidden data={items.data} feed={f} onChange={() => setEditing("shape")} />
      <div className="rows" id="rows">
        {items.isPending ? (
          <p className="empty">Loading…</p>
        ) : items.isError ? (
          <p className="empty">Couldn’t load that view. Try again.</p>
        ) : (
          <Rows items={items.data.items.map((n) => ({ ...n, feed: { id: f.id, name: f.name, owner_name: f.owner_name, mine: f.mine } }))} view={view} since={since} q={query} />
        )}
      </div>
      {items.data && items.data.total > items.data.items.length ? (
        <p className="muted" style={{ color: "var(--ink-3)" }}>
          Showing the first {items.data.items.length} of {items.data.total}.
        </p>
      ) : null}
      <FeedEditor feed={f} open={!!editing} focus={editing === "shape" ? "shape" : undefined} onClose={() => setEditing(false)} />
    </>
  );
}

/** Lowercase for running text, keeping acronyms ("SBIR / STTR", "BAA") as written. */
function lowerWords(text: string) {
  return text.replace(/\S+/g, (w) => (/^[A-Z(][A-Z/()]+\)?$/.test(w) ? w : w.toLowerCase()));
}

function Hidden({ data, feed, onChange }: { data?: FeedItems; feed: Feed; onChange: () => void }) {
  const me = useSession();
  if (!data) return <p className="hidden-note" id="hidden-shape" />;
  const h = data.hidden;
  const skip = data.shape;
  const parts: string[] = [];
  if (h.work) parts.push(`${h.work} ${skip.work.map((w) => WORK_NAMES[w]).join(" or ").toLowerCase()} record${h.work === 1 ? "" : "s"}`);
  if (h.path) parts.push(`${h.path} bought as ${skip.path.map((x) => lowerWords(PATH_NAMES[x] ?? x)).join(" or ")}`);
  if (h.who) parts.push(`${h.who} for ${skip.who.map((g) => skip.who_labels?.[g] ?? g).join(" or ").replace(/^Anyone/, "anyone")}`);
  if (h.named) parts.push(`${h.named} buying a named product`);
  if (h.buyer) parts.push(`${h.buyer} from buyers outside DoW and NATO`);
  if (h.eligibility) parts.push(`${h.eligibility} set aside for firms ${me.tenant?.name} can't bid as`);
  return (
    <p className="hidden-note" id="hidden-shape">
      {parts.length ? (
        <>
          Leaving out {parts.join(", ")}.{" "}
          {feed.editable ? (
            <button className="text-btn" id="edit-shape" onClick={onChange}>
              Change
            </button>
          ) : null}{" "}
        </>
      ) : null}
      {h.query ? (
        <>
          Its search filters leave out {h.query.toLocaleString()} more records that fit its description or
          keywords.{" "}
          {feed.editable ? (
            <Link className="text-btn" id="edit-query" to={`/advanced?feed=${feed.id}`}>
              Edit the search
            </Link>
          ) : null}
        </>
      ) : null}
    </p>
  );
}

function Rows({ items, view, since, q }: { items: NoticeItem[]; view: View; since: string; q: string }) {
  if (!items.length)
    return (
      <p className="empty">
        {q ? (
          <>
            Nothing in this feed matches “{q}”. A feed holds what is still open or ahead; past records, such as an
            industry day already held, are in <Link to={`/search?q=${encodeURIComponent(q)}`}>Search</Link>
            {view === "events" ? "" : ", and upcoming industry and proposers days in Events"}.
          </>
        )
          : view === "upcoming"
            ? "Nothing upcoming matches this feed yet. Forecasts and planning briefings appear here as buyers publish them."
            : view === "events"
              ? "No proposers days, industry days or other events matching this feed are scheduled. They appear here as buyers and agencies announce them."
            : view === "endings"
              ? "No contract matching this feed ends in the next two years. Large DoW contracts appear here six to twenty-four months before they end, when their follow-ons are being planned."
            : view === "soon"
              ? "Nothing with a due date right now. Try Best fit, or widen the feed description."
              : view === "new"
                ? `Nothing new since ${when(since)}.`
                : "Nothing matches yet. Widen the feed description, or mark records you want with Pursue."}
      </p>
    );
  const out: ReactNode[] = [];
  let last = "";
  for (const n of items) {
    if (view === "upcoming" || view === "endings" || view === "events") {
      if (n.group !== last) {
        out.push(
          <div key={"g" + n.group} className="day-label">
            {n.group}
          </div>,
        );
        last = n.group ?? "";
      }
      out.push(
        view === "events" ? <EventRow key={n.key} n={n} /> : view === "endings" && n.ending ? <EndingRow key={n.key} n={n} /> : <UpcomingRow key={n.key} n={n} />,
      );
      continue;
    }
    if (view === "soon") {
      const d = days(n.deadline) ?? 0;
      const label =
        d === 0 ? "Today" : d === 1 ? "Tomorrow" : d < 7 ? fmt(n.deadline, { weekday: "long", month: "short", day: "numeric" }) : d < 14 ? "Next week" : d < 31 ? "Later this month" : "Further out";
      if (label !== last) {
        out.push(
          <div key={"d" + label} className={`day-label ${d <= 1 ? "urgent" : ""}`}>
            {label}
          </div>,
        );
        last = label;
      }
    }
    out.push(<NoticeRow key={n.key} n={n} since={since} />);
  }
  return <>{out}</>;
}
